package com.partejar.app.core.ui.components

import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.changedToUpIgnoreConsumed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntSize
import com.partejar.app.core.ui.theme.PartejarTheme

/** Per-form state used only to distinguish a tap outside the currently focused field. */
class TapOutsideFocusState internal constructor() {
    internal var focusedField: Any? by mutableStateOf(null)
    internal var focusedBounds: Rect? by mutableStateOf(null)
}

@Composable
fun rememberTapOutsideFocusState(): TapOutsideFocusState = remember { TapOutsideFocusState() }

/**
 * Observes a stationary tap without consuming it. A field that receives the tap is
 * excluded by its reported bounds, so switching directly between fields keeps focus uninterrupted.
 */
@Composable
fun Modifier.clearFocusOnTapOutside(state: TapOutsideFocusState): Modifier {
    val focusManager = LocalFocusManager.current
    return pointerInput(state) {
        awaitEachGesture {
        val down = awaitFirstDown(requireUnconsumed = false, pass = PointerEventPass.Initial)
        val startBounds = state.focusedBounds
        var moved = false

        while (true) {
            val event = awaitPointerEvent(PointerEventPass.Final)
            val change = event.changes.firstOrNull { it.id == down.id } ?: break
            if ((change.position - down.position).getDistance() > viewConfiguration.touchSlop) {
                moved = true
            }
            if (change.changedToUpIgnoreConsumed()) {
                if (!moved &&
                    startBounds != null &&
                    !startBounds.contains(down.position) &&
                    state.focusedBounds == startBounds
                ) {
                    focusManager.clearFocus()
                }
                break
            }
            if (!change.pressed) break
        }
        }
    }
}

/** Reports a concrete field's bounds to its containing form; it does not create global state. */
@Composable
fun rememberFocusedFieldBoundsModifier(state: TapOutsideFocusState): Modifier {
    val field = remember { Any() }
    var bounds by remember { mutableStateOf<Rect?>(null) }
    return Modifier
        .onGloballyPositioned { coordinates ->
            bounds = coordinates.boundsInRoot()
            if (state.focusedField === field) state.focusedBounds = bounds
        }
        .onFocusChanged { focus ->
            if (focus.isFocused) {
                state.focusedField = field
                state.focusedBounds = bounds
            } else if (state.focusedField === field) {
                state.focusedField = null
                state.focusedBounds = null
            }
        }
}

/**
 * Requests visibility for a concrete field, reserving optional space immediately below it
 * for the local form actions. Callers opt in only in editable forms that expose actions.
 */
@Composable
fun rememberBringIntoViewOnFocusModifier(
    revealKey: Any? = Unit,
    actionClearance: Dp? = null
): Modifier {
    val requester = remember { BringIntoViewRequester() }
    var focused by remember { mutableStateOf(false) }
    var size by remember { mutableStateOf(IntSize.Zero) }
    val defaultClearance = PartejarTheme.dimensions.minimumTouchTarget + PartejarTheme.spacing.space16
    val clearance = with(LocalDensity.current) { (actionClearance ?: defaultClearance).toPx() }

    LaunchedEffect(focused, revealKey, size, clearance) {
        if (focused && size != IntSize.Zero) {
            requester.bringIntoView(
                Rect(0f, 0f, size.width.toFloat(), size.height + clearance)
            )
        }
    }

    return Modifier
        .bringIntoViewRequester(requester)
        .onSizeChanged { size = it }
        .onFocusChanged { focused = it.isFocused }
}
